// src/components/order/CreateOrderPage.jsx

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db, auth, Storage } from "../../firebase"; 
import { collection, getDocs, updateDoc, doc as firestoreDoc, getDoc, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, listAll } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { ToWords } from 'to-words';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import the child components we built
import ProductSelectionStep from './ProductSelectionStep';
import ShippingStep from './ShippingStep';
import OrderSummary from './OrderSummary';
import ConfirmationModal from './ConfirmationModal';
import ProductSelectionModal from './ProductSelectionModal';

// --- HELPER FUNCTIONS - Moved to top level for clean access ---
const getAbbreviation = (name) => (name || '').split(' ').map(w => w[0]).join('').toUpperCase();

const getStateAbbreviation = (state) => {
    const stateAbbreviations = {'Andhra Pradesh':'AP', 'Arunachal Pradesh':'AR', 'Assam':'AS', 'Bihar':'BR', 'Chhattisgarh':'CG', 'Goa':'GA', 'Gujarat':'GJ', 'Haryana':'HR', 'Himachal Pradesh':'HP', 'Jharkhand':'JH', 'Karnataka':'KA', 'Kerala':'KL', 'Madhya Pradesh':'MP', 'Maharashtra':'MH', 'Manipur':'MN', 'Meghalaya':'ML', 'Mizoram':'MZ', 'Nagaland':'NL', 'Odisha':'OD', 'Punjab':'PB', 'Rajasthan':'RJ', 'Sikkim':'SK', 'Tamil Nadu':'TN', 'Telangana':'TS', 'Tripura':'TR', 'Uttar Pradesh':'UP', 'Uttarakhand':'UK', 'West Bengal':'WB', 'Andaman and Nicobar Islands':'AN', 'Chandigarh':'CH', 'Dadra and Nagar Haveli and Daman and Diu':'DN', 'Lakshadweep':'LD', 'Delhi':'DL', 'Puducherry':'PY', 'Jammu and Kashmir':'JK', 'Ladakh':'LA'};
    return stateAbbreviations[state] || (state || '').substring(0, 2).toUpperCase();
};

const addWrappedText = (doc, text, x, y, maxWidth, lineHeight) => {
    const words = (text || '').split(' ');
    let line = '';
    let currentY = y;
    for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + ' ';
        const testWidth = doc.getStringUnitWidth(testLine) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        if (testWidth > maxWidth && i > 0) {
            doc.text(line.trim(), x, currentY);
            line = words[i] + ' ';
            currentY += lineHeight;
        } else {
            line = testLine;
        }
    }
    if (line.trim() !== '') {
        doc.text(line.trim(), x, currentY);
    }
    return currentY + lineHeight;
};


const CreateOrderPage = ({ orderType = 'regular' }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { design } = location.state || {};
    
    // --- STATE MANAGEMENT ---
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState(design ? [{ ...design, quantity: 1, selectedSize: "" }] : []);
    const [formData, setFormData] = useState({ ReferenceID: "", name: "", email: "", phone: "", address: "", city: "", pincode: "", state: "" });
    const [paymentMethod, setPaymentMethod] = useState('Prepaid');
    const [isProductModalOpen, setProductModalOpen] = useState(false);
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [walletBalance, setWalletBalance] = useState(0);
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [neckLabelsEnabled, setNeckLabelsEnabled] = useState(false);
    const [shippingPreference, setShippingPreference] = useState("");
    const [partiallyPaidAmount, setPartiallyPaidAmount] = useState(0);
    const [calculatedTotal, setCalculatedTotal] = useState(0);

    const pageTitle = orderType === 'sample' ? 'Create Sample Order' : 'Create New Order';

    // --- DATA FETCHING & AUTH ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                fetchUserAndProductData(currentUser.uid);
            } else {
                toast.error('User not authenticated.');
                navigate('/login');
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    const fetchUserAndProductData = async (uid) => {
        setLoading(true);
        try {
            const productsCollection = collection(db, 'users', uid, 'designs');
            const productsSnapshot = await getDocs(productsCollection);
            setProducts(productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            const userRef = firestoreDoc(db, 'users', uid);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
                const data = userDoc.data();
                setUserData(data);
                setNeckLabelsEnabled(data.neckLabels === true);
            } else {
                 toast.error("User profile data not found.");
            }

            const walletRef = firestoreDoc(db, 'users', uid, 'wallet', 'walletDetails');
            const walletSnap = await getDoc(walletRef);
            if (walletSnap.exists()) setWalletBalance(walletSnap.data().balance);

        } catch (error) {
            console.error("Error fetching initial data:", error);
            toast.error("Failed to load necessary data.");
        } finally {
            setLoading(false);
        }
    };
    
    // --- More Helpers ---
    const getSKUForSize = (skus, size) => {
        if (!skus || !Array.isArray(skus)) return null;
        return skus.find(sku => {
            const parts = sku.split('-');
            return (parts.length >= 3 && parts[parts.length - 2] === size) || (parts[parts.length - 1] === size);
        });
    };
    
    const findExistingProduct = (productsList, newProduct) => {
        return productsList.find(p => p.id === newProduct.id && p.selectedSize === newProduct.selectedSize && JSON.stringify(p.variantData) === JSON.stringify(newProduct.variantData));
    };

    const toWords = new ToWords({
        localeCode: "en-IN",
        converterOptions: { currency: true, currencyOptions: { name: "Rupee", plural: "Rupees", symbol: "₹", fractionalUnit: { name: "Paisa", plural: "Paise", symbol: "" }}},
    });
    
    // --- CALCULATIONS ---
    const neckLabelPrice = neckLabelsEnabled ? 5 : 0; // 5 Rs per unit only if neck labels are enabled
    // Calculate base subtotal (what brand charges customer)
    let baseSubTotal = selectedProducts.reduce((sum, p) => sum + (p.sellingPrice * p.quantity) + (neckLabelPrice * p.quantity), 0);
    // COD amount to collect from customer (editable if COD)
    const [codCollectAmount, setCodCollectAmount] = useState(baseSubTotal);
    // Calculate totalPrice (what we charge brand) - p.totalPrice is already the base design price
    const baseTotalPrice = selectedProducts.reduce((sum, p) => sum + p.totalPrice * p.quantity, 0);

    // Calculate volumetric weight and slabs
    function calculateVolumetricWeight(selectedProducts) {
        const baseDimensions = { L: 25, B: 18, H: 3 }; // Base dimensions for the first 2 items
        const stepIncrease = { L: 4, B: 4, H: 1 }; // Dimensions increase per additional 2 products
        const weightPerTShirt = 0.25; // Since 2 T-shirts make 1 slab of 0.5kg
        const weightPerBabyTee = 0.167; // Since 3 Baby Tees make 1 slab of 0.5kg
        let totalWeight = 0;
        let maxLength = baseDimensions.L;
        let maxBreadth = baseDimensions.B;
        let maxHeight = baseDimensions.H;
        if (!Array.isArray(selectedProducts) || selectedProducts.length === 0) {
            return {
                length: baseDimensions.L,
                breadth: baseDimensions.B,
                height: baseDimensions.H,
                weight: 0.5,
                slabs: 1,
            };
        }
        selectedProducts.forEach((product) => {
            const quantity = product.quantity;
            const name = product.productName?.toLowerCase() || "";
            if (quantity && typeof quantity === "number" && quantity > 0) {
                if (name.includes("t-shirt")) {
                    totalWeight += quantity * weightPerTShirt;
                } else if (name.includes("baby tee")) {
                    totalWeight += quantity * weightPerBabyTee;
                } else {
                    // For other products, assume 1 slab per 2 items, 0.5kg per slab
                    const slabs = Math.ceil(quantity / 2);
                    totalWeight += slabs * 0.5;
                }
            }
        });
        if (totalWeight <= 0) totalWeight = 0.5;
        const totalSlabs = Math.ceil(totalWeight / 0.5);
        maxLength = baseDimensions.L + (totalSlabs - 1) * stepIncrease.L;
        maxBreadth = baseDimensions.B + (totalSlabs - 1) * stepIncrease.B;
        maxHeight = baseDimensions.H + (totalSlabs - 1) * stepIncrease.H;
        return {
            length: maxLength,
            breadth: maxBreadth,
            height: maxHeight,
            weight: parseFloat(totalWeight.toFixed(3)),
            slabs: totalSlabs,
        };
    }


//     function calculateVolumetricWeight(selectedProducts) {
//     const baseDimensions = { L: 25, B: 18, H: 3 }; // Base dimensions for the first 2 items
//     const stepIncrease = { L: 4, B: 4, H: 1 }; // Dimensions increase per additional 2 products
//     const weightPerTShirt = 0.5; // 1 T-shirt = 0.5kg (changed from 0.25)
//     const weightPerBabyTee = 0.25; // 1 Baby Tee = 0.25kg (changed from 0.167, so 2 Baby Tees = 0.5kg)
    
//     let totalWeight = 0;
//     let maxLength = baseDimensions.L;
//     let maxBreadth = baseDimensions.B;
//     let maxHeight = baseDimensions.H;
    
//     if (!Array.isArray(selectedProducts) || selectedProducts.length === 0) {
//         return {
//             length: baseDimensions.L,
//             breadth: baseDimensions.B,
//             height: baseDimensions.H,
//             weight: 0.5,
//             slabs: 1,
//         };
//     }
    
//     selectedProducts.forEach((product) => {
//         const quantity = product.quantity;
//         const name = product.productName?.toLowerCase() || "";
//         if (quantity && typeof quantity === "number" && quantity > 0) {
//             if (name.includes("t-shirt")) {
//                 totalWeight += quantity * weightPerTShirt;
//             } else if (name.includes("baby tee")) {
//                 totalWeight += quantity * weightPerBabyTee;
//             } else {
//                 // For other products, assume 1 slab per 2 items, 0.5kg per slab
//                 const slabs = Math.ceil(quantity / 2);
//                 totalWeight += slabs * 0.5;
//             }
//         }
//     });
    
//     if (totalWeight <= 0) totalWeight = 0.5;
//     const totalSlabs = Math.ceil(totalWeight / 0.5);
//     maxLength = baseDimensions.L + (totalSlabs - 1) * stepIncrease.L;
//     maxBreadth = baseDimensions.B + (totalSlabs - 1) * stepIncrease.B;
//     maxHeight = baseDimensions.H + (totalSlabs - 1) * stepIncrease.H;
    
//     return {
//         length: maxLength,
//         breadth: maxBreadth,
//         height: maxHeight,
//         weight: parseFloat(totalWeight.toFixed(3)),
//         slabs: totalSlabs,
//     };
// }

   

// Calculate shipping charge using volumetric weight, slabs, payment, shippingPreference, and state
   

// const calculateShippingCharge = () => {
//         if (!userData || !formData.state || !shippingPreference) return 0;
//         const specialStates = [
//             "North East", "J & K", "Ladakh", "Andaman & Nicobar", "Himachal Pradesh",
//             "Sikkim", "Lakshadweep", "Arunachal Pradesh", "Assam", "Manipur",
//             "Meghalaya", "Mizoram", "Nagaland", "Tripura"
//         ];
//         const billingState = formData.state;
//         const { slabs } = calculateVolumetricWeight(selectedProducts);
//         let baseRate = 0;
//         if (shippingPreference === "Air") baseRate = 75;
//         else if (shippingPreference === "Surface") baseRate = 65;
//         else baseRate = specialStates.includes(billingState) ? 85 : 65;
//         let shippingCharge = baseRate * slabs;
//         const sub_total = selectedProducts.reduce((total, product) => total + product.sellingPrice * product.quantity, 0);
//         if (paymentMethod === "COD" || paymentMethod === "Partially Paid") shippingCharge += (sub_total > 2000) ? (sub_total * 0.025) : 50;
//         return shippingCharge;
//     };

    const calculateShippingCharge = () => {
    if (!userData || !formData.state || !shippingPreference) return 0;
    
    const specialStates = [
        "North East", "J & K", "Ladakh", "Andaman & Nicobar", "Himachal Pradesh",
        "Sikkim", "Lakshadweep", "Arunachal Pradesh", "Assam", "Manipur",
        "Meghalaya", "Mizoram", "Nagaland", "Tripura"
    ];
    
    const billingState = formData.state;
    const { slabs } = calculateVolumetricWeight(selectedProducts);    
    let baseRate = 0;
    if (shippingPreference === "Air") baseRate = 75;
    else if (shippingPreference === "Surface") baseRate = 65;
    else baseRate = specialStates.includes(billingState) ? 85 : 65;   
    let shippingCharge = baseRate * slabs;   
    const sub_total = selectedProducts.reduce((total, product) => total + product.sellingPrice * product.quantity, 0);   
    if (paymentMethod === "COD" || paymentMethod === "Partially Paid") {
        shippingCharge += (sub_total > 2000) ? (sub_total * 0.025) : 50;
    }
    return shippingCharge;
};

    const shippingChargeValue = calculateShippingCharge();
    const shippingTax = shippingChargeValue * 0.18;
    const totalShipping = shippingChargeValue + shippingTax;
    
    // Calculate neck label total for tax purposes
    const neckLabelTotal = selectedProducts.reduce((sum, p) => sum + (neckLabelPrice * p.quantity), 0);
    const taxableAmount = baseTotalPrice + neckLabelTotal;
    const orderTax = taxableAmount * 0.05;
    const totalOrderCost = baseTotalPrice + neckLabelTotal + orderTax + totalShipping;
    const confirmBalance = walletBalance - totalOrderCost;

    // --- Shipping Cost Calculation ---
    let shippingCost = 0;
    let shippingLabel = "";
    if (shippingPreference === "Air") {
        shippingCost = 75;
        shippingLabel = "Air - ₹75";
    } else if (shippingPreference === "Surface") {
        shippingCost = 65;
        shippingLabel = "Surface - ₹65";
    } else {
        shippingLabel = "Select Method";
    }

    // SKU handling here properly
const handleSelectProduct = (products) => {
    if (!Array.isArray(products)) {
        toast.error("Invalid product selection format.");
        return;
    }

    const newProducts = [];

    for (const product of products) {
        if (!product.selectedSize) {
            toast.error(`Size not selected for product ${product.designName}.`);
            continue;
        }

        const selectedSKU = getSKUForSize(product.SKUs, product.selectedSize);
        if (!selectedSKU) {
            toast.error(`SKU not found for size ${product.selectedSize} in ${product.designName}.`);
            continue;
        }

        const newProduct = {
            ...product,
            selectedSKU,
            quantity: 1,
        };

        newProducts.push(newProduct);
    }

    setSelectedProducts(prev => {
        const merged = [...prev];

        for (const newProd of newProducts) {
            const existingIndex = merged.findIndex(p =>
                p.id === newProd.id && p.selectedSize === newProd.selectedSize
            );

            if (existingIndex !== -1) {
                // If already exists, increment quantity
                merged[existingIndex].quantity += 1;
            } else {
                merged.push(newProd);
            }
        }

        return merged;
    });

    setProductModalOpen(false);
};

    const handleOrderSubmit = () => {
        if (selectedProducts.length === 0) return toast.warn("Please add products to your order.");
        if (Object.values(formData).some(val => val.trim() === "")) return toast.warn("Please fill all shipping details.");
        if (walletBalance < totalOrderCost) return toast.error('Insufficient wallet balance.');
        setConfirmModalOpen(true);
    };

    // --- PDF Generation and Upload ---
    const generateAndUploadInvoice = async (orderData, brandData) => {
        // This function now uses the arguments passed to it, not the component's state.
        const financialYear = '25-26';
        const brandAbbreviation = getAbbreviation(brandData.brandName);
        const stateAbbreviation = getStateAbbreviation(brandData.state);
        const brandFolderPath = `Sales_invoice/${brandAbbreviation}2026/`;
        const salesInvoiceStorageRef = ref(Storage, brandFolderPath);

        const salesListResult = await listAll(salesInvoiceStorageRef);
        let nextSalesInvoiceNumber = 1;
        if (salesListResult.items.length > 0) {
            const salesInvoiceNumbers = salesListResult.items
                .map(item => item.name.match(new RegExp(`${brandAbbreviation}_${stateAbbreviation}_${financialYear}_(\\d+)\\.pdf$`)))
                .filter(match => match)
                .map(match => parseInt(match[1], 10));
            if (salesInvoiceNumbers.length > 0) nextSalesInvoiceNumber = Math.max(...salesInvoiceNumbers) + 1;
        }

        const salesInvoiceNumberStr = String(nextSalesInvoiceNumber).padStart(3, '0');
        const salesInvoiceNumber = `${brandAbbreviation}/${stateAbbreviation}/${financialYear}/${salesInvoiceNumberStr}`;
        const salesInvoiceFileName = `${brandAbbreviation}_${stateAbbreviation}_${financialYear}_${salesInvoiceNumberStr}.pdf`;

        const salesDoc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [100, 150] });
        const margin = 3, width = 94, startX = margin, startY = margin, lineHeight = 3.5;
        let currentY = startY;
        const invoiceDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });

        salesDoc.setFontSize(10);
        salesDoc.setFont("Helvetica", "bold");
        salesDoc.text('TAX Invoice', width / 2 + startX, currentY += lineHeight, null, null, 'center');
        currentY += lineHeight;
        
        salesDoc.setFontSize(7);
        salesDoc.setFont("Helvetica", "normal");
        salesDoc.text(`Date: ${invoiceDate}`, startX, currentY += lineHeight);
        salesDoc.text(`Invoice #: ${salesInvoiceNumber}`, startX + width / 2, currentY);
        currentY += lineHeight / 2;
        salesDoc.line(startX, currentY, startX + width, currentY);
        currentY += lineHeight / 2;

        const halfWidth = width / 2 - 2;
        // Bill From (Brand Info)
        salesDoc.setFont("Helvetica", "bold");
        let billFromY = currentY + lineHeight;
        salesDoc.text("Bill From:", startX, billFromY);
        salesDoc.setFont("Helvetica", "normal");
        billFromY += lineHeight;
        salesDoc.text(`${brandData.brandName}`, startX, billFromY);
        billFromY += lineHeight;
        const addressMaxWidth = halfWidth - 10;
        billFromY = addWrappedText(salesDoc, `${brandData.address}`, startX, billFromY, addressMaxWidth, lineHeight);
        billFromY += lineHeight;
        // Group state, pincode on one line
        salesDoc.text(`${brandData.state}, ${brandData.pincode}`, startX, billFromY);
        billFromY += lineHeight;
        // Always render GST line, even if missing
        salesDoc.text(`GST: ${brandData.gstNumber ? brandData.gstNumber : ''}`, startX, billFromY);
        billFromY += lineHeight;
        // Ship From (fixed offset)
        const shipFromStartY = currentY + lineHeight;
        let shipFromY = shipFromStartY;
        salesDoc.setFont("Helvetica", "bold");
        salesDoc.text("Ship From:", startX + halfWidth + 5, shipFromY);
        salesDoc.setFont("Helvetica", "normal");
        shipFromY += lineHeight;
        salesDoc.text("Jalandhar", startX + halfWidth + 5, shipFromY);
        shipFromY += lineHeight;
        salesDoc.text("Punjab", startX + halfWidth + 5, shipFromY);
        shipFromY += lineHeight;
        salesDoc.text("144004", startX + halfWidth + 5, shipFromY);
        // After both, set currentY to the max
        currentY = Math.max(billFromY, shipFromY);
        salesDoc.line(startX, currentY, startX + width, currentY);
        currentY += lineHeight / 2;
        // Bill To and Ship To
        const billToShipToStartY = currentY;
        salesDoc.setFont("Helvetica", "bold");
        let billToY = billToShipToStartY + lineHeight;
        salesDoc.text("Bill To:", startX, billToY);
        salesDoc.setFont("Helvetica", "normal");
        billToY += lineHeight;
        salesDoc.text(`${orderData.customer.name}`, startX, billToY);
        billToY += lineHeight;
        billToY = addWrappedText(salesDoc, `${orderData.customer.address}`, startX, billToY, addressMaxWidth, lineHeight);
        billToY += lineHeight;
        // Group city, state, pincode on one line
        salesDoc.text(`${orderData.customer.city}, ${orderData.customer.state}, ${orderData.customer.pinCode}`, startX, billToY);
        billToY += lineHeight;
        // Ship To
        salesDoc.setFont("Helvetica", "bold");
        let shipToY = billToShipToStartY + lineHeight;
        salesDoc.text("Ship To:", startX + halfWidth + 5, shipToY);
        salesDoc.setFont("Helvetica", "normal");
        shipToY += lineHeight;
        salesDoc.text(`${orderData.customer.name}`, startX + halfWidth + 5, shipToY);
        shipToY += lineHeight;
        shipToY = addWrappedText(salesDoc, `${orderData.customer.address}`, startX + halfWidth + 5, shipToY, addressMaxWidth, lineHeight);
        shipToY += lineHeight;
        // Group city, state, pincode on one line
        salesDoc.text(`${orderData.customer.city}, ${orderData.customer.state}, ${orderData.customer.pinCode}`, startX + halfWidth + 5, shipToY);
        shipToY += lineHeight;
        // After both, set currentY to the max
        currentY = Math.max(billToY, shipToY);
        salesDoc.line(startX, currentY, startX + width, currentY);
        currentY += lineHeight;
        
        salesDoc.setFont("Helvetica", "bold");
        salesDoc.text("ITEMS", startX, currentY);
        salesDoc.setFontSize(6);
        salesDoc.text("HSN", startX + 35, currentY, null, null, 'center');
        salesDoc.text("Qty", startX + 50, currentY, null, null, 'center');
        salesDoc.text("Price", startX + 65, currentY, null, null, 'center');
        salesDoc.text("Tax", startX + 80, currentY, null, null, 'center');
        salesDoc.text("Amount", startX + width, currentY, null, null, 'right');
        currentY += lineHeight / 2;
        salesDoc.line(startX, currentY, startX + width, currentY);
        currentY += lineHeight / 2;

        // --- SUMMARY CALCULATIONS ---
        let totalSubtotal = 0, totalTaxableValue = 0, totalCGST = 0, totalSGST = 0, totalIGST = 0, totalTaxAmount = 0;
        salesDoc.setFont("Helvetica", "normal");

        orderData.products.forEach(product => {
            // Use addWrappedText for product name and get the new Y
            const productName = (product.productName || 'Unknown').substring(0, 18);
            const firstLineY = currentY + lineHeight;
            let nameEndY = addWrappedText(salesDoc, productName, startX, firstLineY, 30, lineHeight);
            // Use the first line Y for other columns
            salesDoc.text('610910', startX + 35, firstLineY, null, null, 'center');
            salesDoc.text(product.quantity.toString(), startX + 50, firstLineY, null, null, 'center');
            // Calculate the individual item price (total including tax)
            const itemPrice = (parseFloat(product.sellingPrice) || 0);
            totalSubtotal += itemPrice;
            // Determine tax rate based on item's price
            const taxRate = ((itemPrice / product.quantity) >= 1000) ? 0.12 : 0.05;
            const taxMultiplier = 1 + taxRate;
            const taxableValue = itemPrice / taxMultiplier;
            totalTaxableValue += taxableValue;
            let cgstAmt = 0, sgstAmt = 0, igstAmt = 0, taxAmount = 0, taxDisplay = "";
            if (brandData.state === "Maharashtra") {
                cgstAmt = taxableValue * (taxRate / 2);
                sgstAmt = taxableValue * (taxRate / 2);
                taxAmount = cgstAmt + sgstAmt;
                totalCGST += cgstAmt;
                totalSGST += sgstAmt;
                taxDisplay = `${(taxRate * 100).toFixed(0)}% (C+S)`;
            } else {
                igstAmt = taxableValue * taxRate;
                taxAmount = igstAmt;
                totalIGST += igstAmt;
                taxDisplay = `${(taxRate * 100).toFixed(0)}% (IGST)`;
            }
            totalTaxAmount += taxAmount;
            salesDoc.text((itemPrice / product.quantity).toFixed(2), startX + 65, firstLineY, null, null, 'center');
            salesDoc.text(taxDisplay, startX + 80, firstLineY, null, null, 'center');
            salesDoc.text(itemPrice.toFixed(2), startX + width, firstLineY, null, null, 'right');
            // Advance currentY by the height of the wrapped name or at least one lineHeight
            currentY = Math.max(nameEndY, firstLineY + lineHeight);
        });
        // After items, always advance currentY by lineHeight before summary
        currentY += lineHeight;
        salesDoc.line(startX, currentY, startX + width, currentY);
        currentY += lineHeight / 2;
        // Add extra lineHeight before Bill To/Ship To and ITEMS header
        currentY += lineHeight;
        salesDoc.setFont("Helvetica", "bold");
        salesDoc.text("SUMMARY", startX, currentY += lineHeight);
        currentY += lineHeight / 2;
        salesDoc.line(startX, currentY, startX + width, currentY);
        currentY += lineHeight;
        salesDoc.setFontSize(7);
        salesDoc.setFont("Helvetica", "normal");
        salesDoc.text("Subtotal (Excl. Tax):", startX, currentY);
        salesDoc.text(totalTaxableValue.toFixed(2), startX + width, currentY, null, null, 'right');
        currentY += lineHeight;
        if (brandData.state === "Maharashtra") {
            salesDoc.text("CGST:", startX, currentY);
            salesDoc.text(totalCGST.toFixed(2), startX + width, currentY, null, null, 'right');
            currentY += lineHeight;
            salesDoc.text("SGST:", startX, currentY);
            salesDoc.text(totalSGST.toFixed(2), startX + width, currentY, null, null, 'right');
            currentY += lineHeight;
        } else {
            salesDoc.text("IGST:", startX, currentY);
            salesDoc.text(totalIGST.toFixed(2), startX + width, currentY, null, null, 'right');
            currentY += lineHeight;
        }
        salesDoc.text("Total Tax:", startX, currentY);
        salesDoc.text((totalCGST + totalSGST + totalIGST).toFixed(2), startX + width, currentY, null, null, 'right');
        currentY += lineHeight;
        salesDoc.line(startX, currentY, startX + width, currentY);
        currentY += lineHeight / 2;
        salesDoc.setFontSize(9);
        salesDoc.setFont("Helvetica", "bold");
        salesDoc.text("TOTAL:", startX, currentY += lineHeight);
        salesDoc.text(totalSubtotal.toFixed(2), startX + width, currentY, null, null, 'right');
        currentY += lineHeight;
        salesDoc.setFontSize(6);
        salesDoc.setFont("Helvetica", "normal");
        const amountInWords = toWords.convert(totalSubtotal);
        currentY = addWrappedText(salesDoc, `Amount in words: ${amountInWords}`, startX, currentY, width, lineHeight);
        // Footer notes
        currentY += lineHeight / 2;
        salesDoc.line(startX, currentY, startX + width, currentY);
        currentY += lineHeight / 2;
        salesDoc.setFontSize(6);
        salesDoc.text("Note: All prices include applicable taxes.", startX, currentY += lineHeight);
        salesDoc.text("Thanks for your business.", startX, currentY += lineHeight);
        // Signature line
        currentY += lineHeight;
        salesDoc.line(startX + width / 4, currentY, startX + (width * 3) / 4, currentY);
        currentY += lineHeight;
        salesDoc.text("Authorized Signature", startX + width / 2, currentY, null, null, 'center');

        const pdfBlob = salesDoc.output('blob');
        const uploadRef = ref(Storage, `${brandFolderPath}${salesInvoiceFileName}`);
        await uploadBytesResumable(uploadRef, pdfBlob);
        return getDownloadURL(uploadRef);
    };

    // --- MAIN ORDER CONFIRMATION LOGIC ---
    const handleConfirmOrder = async () => {
        if (isProcessing || !user || !userData) {
            toast.error("Cannot process order. User data is missing.");
            return;
        }
        if (!shippingPreference) {
            toast.error("Please select a shipping preference.", { autoClose: 3000 });
            return;
        }
        // if (paymentMethod === 'COD' && (!codCollectAmount || isNaN(codCollectAmount) || codCollectAmount < 1)) {
        //     toast.error("Please enter a valid COD amount (minimum 1).", { autoClose: 3000 });
        //     return;
        // }
        setIsProcessing(true);
        try {
            // Fetch design data for each product
            const productsWithDesign = [];
            for (const p of selectedProducts) {
                const designSnapshot = await getDoc(firestoreDoc(db, "users", user.uid, "designs", p.id));
                if (!designSnapshot.exists()) {
                    throw new Error(`Design data not found for product ID: ${p.id}`);
                }
                productsWithDesign.push({ ...p, design: designSnapshot.data() });
            }

            // Determine pickup location based on first product's design
            let pickupLocation = "Printing Unit";
            if (productsWithDesign.length > 0) {
                const primaryDesignData = productsWithDesign[0].design;
                if (primaryDesignData.printMethod === "DTG Epson" || primaryDesignData.printMethod === "DTF Epson") {
                    pickupLocation = "Printing Unit 2";
                } else if (primaryDesignData.printMethod === "DTG Brother") {
                    pickupLocation = "Printing Unit";
                }
            }

            const { length, breadth, height, weight } = calculateVolumetricWeight(selectedProducts);
            const codCharges = paymentMethod === "COD" ? (userData.codCharges || 0) : 0;
            const productSellingPriceSum = selectedProducts.reduce((sum, p) => sum + (parseFloat(p.sellingPrice) || 0) * p.quantity, 0);
            let sub_total = productSellingPriceSum;
            if (paymentMethod === 'Partially Paid') {
                sub_total = Math.max(productSellingPriceSum - partiallyPaidAmount, 0);
            }
            const totalPrice = calculatedTotal;

            // Debug: Log shipping and payment info before building order data
            console.log('DEBUG: totalShipping:', totalShipping, 'paymentMethod:', paymentMethod, 'selectedProducts:', selectedProducts);
            if (totalShipping === 0 && selectedProducts.length > 0) {
                toast.warn('Warning: Shipping is 0. Please check shipping method and address.');
            }

            const finalOrderData = {
                order_id: `${Date.now() % 1000000000}${Math.floor(Math.random() * 100)}`,
                Storeorder_id: formData.ReferenceID,
                customer: { name: formData.name, email: formData.email, address: formData.address, city: formData.city, state: formData.state, pinCode: formData.pincode, phone: formData.phone },
                products: productsWithDesign.map(p => ({
                    designName: p.designName, productName: p.productName, sellingPrice: p.sellingPrice * p.quantity,
                    quantity: p.quantity, SKU: p.selectedSKU, selectedSize: p.selectedSize,
                    designTotalPrice: p.totalPrice, neckLabelPrice, itemTotalPrice: p.totalPrice + neckLabelPrice,
                    design: p.design, // Attach correct design data for each product
                })),
                sub_total: sub_total,
                totalPrice: totalPrice,
                UniteeShipping: totalShipping,
                payment: paymentMethod,
                pickupLocation,
                userId: user.uid,
                status: 'confirmed',
                length, breadth, height, weight,
                isPaid: true,
                timestamp: Timestamp.now(),
                shippingPreference,
                shippingCost,
                shippingLabel,
            };

            const salesInvoiceUrl = await generateAndUploadInvoice(finalOrderData, userData);
            finalOrderData.salesInvoiceUrl = salesInvoiceUrl;

            console.log("Final Order Data:", finalOrderData);

            await axios.post('https://ufb-1.onrender.com/create-sample-shipmozo-order', finalOrderData, { headers: { 'Content-Type': 'application/json' } });

            const walletRef = firestoreDoc(db, 'users', user.uid, 'wallet', 'walletDetails');
            await updateDoc(walletRef, { balance: confirmBalance });

            const transactionsRef = collection(walletRef, 'transactions');
            await addDoc(transactionsRef, {
                type: finalOrderData.order_id, amount: totalPrice, balanceBefore: walletBalance,
                balanceAfter: confirmBalance, timestamp: Timestamp.now(),
            });

            toast.success("Order submitted successfully!");

                    try {
                const inventoryRes = await axios.post(
                    'https://ufb-1.onrender.com/api/inventory/update-stock-from-order',
                    {
                        mode: 'create',
                        orderData: finalOrderData
                    },
                    { headers: { 'Content-Type': 'application/json' } }
                );
                console.log('Inventory update response:', inventoryRes.data);
            } catch (invErr) {
                console.error('Inventory update failed:', invErr);
            }
            
            navigate('/PaidOrders');

        } catch (error) {
            console.error('Error during order confirmation:', error);
            const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'An unknown error occurred.';
            toast.error(`Order failed: ${errorMessage}`);
        } finally {
            setIsProcessing(false);
            setConfirmModalOpen(false);
        }
    };

    if (loading) return <div className="flex items-center justify-center h-screen bg-gray-100">Loading...</div>;


    return (
        <div className="bg-gray-100 min-h-screen">
            <div className="container mx-auto p-4 md:p-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">{pageTitle}</h1>
                    <p className="text-gray-600">Complete the details below to place your order.</p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-8">
                    <div className="lg:col-span-2 space-y-8">
                        <div className="bg-white p-6 rounded-lg shadow-md">
                           <ProductSelectionStep selectedProducts={selectedProducts} setSelectedProducts={setSelectedProducts} onAddProductClick={() => setProductModalOpen(true)} neckLabelPrice={neckLabelPrice} />
                        </div>
                        <div id="shipping-details-section" className="bg-white p-6 rounded-lg shadow-md">
                            <ShippingStep formData={formData} setFormData={setFormData} />
                        </div>
                    </div>
                    <div className="lg:col-span-1 mt-8 lg:mt-0">
                        <div className="sticky top-6">
                           <OrderSummary
                               pricing={{ subtotal: baseTotalPrice, shipping: totalShipping, tax: orderTax, total: calculatedTotal }}
                               paymentMethod={paymentMethod}
                               setPaymentMethod={setPaymentMethod}
                               onSubmit={handleOrderSubmit}
                               isSubmitDisabled={selectedProducts.length === 0 || isProcessing}
                               codCollectAmount={codCollectAmount}
                               setCodCollectAmount={setCodCollectAmount}
                               shippingPreference={shippingPreference}
                               setShippingPreference={setShippingPreference}
                               selectedProducts={selectedProducts}
                               neckLabelPrice={neckLabelPrice}
                               partiallyPaidAmount={partiallyPaidAmount}
                               setPartiallyPaidAmount={setPartiallyPaidAmount}
                               onTotalChange={setCalculatedTotal}
                               formData={formData}
                           />
                        </div>
                    </div>
                </div>
            </div>
            <ProductSelectionModal isOpen={isProductModalOpen} onClose={() => setProductModalOpen(false)} products={products} onSelectProduct={handleSelectProduct} />
            <ConfirmationModal isOpen={isConfirmModalOpen} onClose={() => setConfirmModalOpen(false)} onConfirm={handleConfirmOrder} isProcessing={isProcessing} totalCost={calculatedTotal} />
            <ToastContainer position="bottom-right" autoClose={5000} />
        </div>
    );
};

export default CreateOrderPage;